/**
 * Subscription Limit Utilities
 * 
 * This module provides functions to check subscription plan limits
 * and enforce restrictions based on user's subscription plan.
 */

import { supabase } from '../supabaseClient';
import type { Profile } from '../types';

export type PlanName = 'free' | 'civilian_plus' | 'civilian_pro' | 'lawyer_pro' | 'firm' | 'insurance_partnership';

export interface SubscriptionLimits {
    maxChatsPerDay: number | null; // null = unlimited
    maxDocumentPages: number | null; // null = unlimited
    maxCases: number | null; // null = unlimited
    watermarkPDFs: boolean;
    textToSpeech: boolean;
    emailGeneration: boolean;
    shareLinks: boolean;
    priorityOCR: boolean;
    batchAnalysis: boolean;
    exportToWord: boolean;
    clauseHeatmaps: boolean;
    apiAccess: boolean;
}

/**
 * Get subscription limits for a given plan
 */
export function getPlanLimits(planName: PlanName | string | null | undefined): SubscriptionLimits {
    // Default to free plan if no plan specified
    const plan = (planName || 'free') as PlanName;

    const limits: Record<PlanName, SubscriptionLimits> = {
        free: {
            maxChatsPerDay: 5,
            maxDocumentPages: 5,
            maxCases: 3,
            watermarkPDFs: true,
            textToSpeech: false,
            emailGeneration: false,
            shareLinks: false,
            priorityOCR: false,
            batchAnalysis: false,
            exportToWord: false,
            clauseHeatmaps: false,
            apiAccess: false,
        },
        civilian_plus: {
            maxChatsPerDay: null, // Unlimited (fair-use)
            maxDocumentPages: 50,
            maxCases: null, // Unlimited
            watermarkPDFs: false,
            textToSpeech: true,
            emailGeneration: true,
            shareLinks: false,
            priorityOCR: false,
            batchAnalysis: false,
            exportToWord: false,
            clauseHeatmaps: false,
            apiAccess: false,
        },
        civilian_pro: {
            maxChatsPerDay: null, // Unlimited
            maxDocumentPages: 150,
            maxCases: null, // Unlimited
            watermarkPDFs: false,
            textToSpeech: true,
            emailGeneration: true,
            shareLinks: true,
            priorityOCR: true,
            batchAnalysis: false,
            exportToWord: false,
            clauseHeatmaps: false,
            apiAccess: false,
        },
        lawyer_pro: {
            maxChatsPerDay: null, // Unlimited
            maxDocumentPages: null, // Unlimited
            maxCases: null, // Unlimited
            watermarkPDFs: false,
            textToSpeech: true,
            emailGeneration: true,
            shareLinks: true,
            priorityOCR: true,
            batchAnalysis: true,
            exportToWord: true,
            clauseHeatmaps: true,
            apiAccess: true,
        },
        firm: {
            maxChatsPerDay: null, // Unlimited
            maxDocumentPages: null, // Unlimited
            maxCases: null, // Unlimited
            watermarkPDFs: false,
            textToSpeech: true,
            emailGeneration: true,
            shareLinks: true,
            priorityOCR: true,
            batchAnalysis: true,
            exportToWord: true,
            clauseHeatmaps: true,
            apiAccess: true,
        },
        insurance_partnership: {
            maxChatsPerDay: null, // Unlimited
            maxDocumentPages: null, // Unlimited
            maxCases: null, // Unlimited
            watermarkPDFs: false,
            textToSpeech: true,
            emailGeneration: true,
            shareLinks: true,
            priorityOCR: true,
            batchAnalysis: true,
            exportToWord: true,
            clauseHeatmaps: true,
            apiAccess: true,
        },
    };

    return limits[plan] || limits.free;
}

/**
 * Check if user can send a chat message (based on daily limit)
 */
export async function canSendChatMessage(
    userId: string,
    planName: PlanName | string | null | undefined
): Promise<{ allowed: boolean; reason?: string; remaining?: number }> {
    const limits = getPlanLimits(planName);

    // Unlimited plans
    if (limits.maxChatsPerDay === null) {
        return { allowed: true };
    }

    // Check daily chat count
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    try {
        const { count, error } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId)
            .eq('sender', 'user')
            .gte('created_at', today.toISOString())
            .lt('created_at', tomorrow.toISOString());

        if (error) {
            console.error('Error checking chat limit:', error);
            // Allow on error to avoid blocking users
            return { allowed: true };
        }

        const chatCount = count || 0;
        const remaining = limits.maxChatsPerDay - chatCount;

        if (chatCount >= limits.maxChatsPerDay) {
            return {
                allowed: false,
                reason: `You've reached your daily chat limit of ${limits.maxChatsPerDay} messages. Upgrade to continue.`,
                remaining: 0,
            };
        }

        return {
            allowed: true,
            remaining: remaining,
        };
    } catch (error) {
        console.error('Error checking chat limit:', error);
        // Allow on error to avoid blocking users
        return { allowed: true };
    }
}

/**
 * Check if user can upload a document (based on page limit)
 * 
 * Note: For PDF files, this performs client-side page counting.
 * The backend Edge Function will also validate page limits server-side.
 */
export function canUploadDocument(
    file: File,
    planName: PlanName | string | null | undefined
): Promise<{ allowed: boolean; reason?: string; maxPages?: number | null }> {
    return new Promise((resolve) => {
        const limits = getPlanLimits(planName);

        // Unlimited plans
        if (limits.maxDocumentPages === null) {
            resolve({ allowed: true });
            return;
        }

        // For PDF files, we need to count pages
        if (file.type === 'application/pdf') {
            // Use a timeout to prevent hanging if PDF.js takes too long
            const timeout = setTimeout(() => {
                console.warn('PDF page count timeout - allowing upload (backend will validate)');
                resolve({ allowed: true });
            }, 10000); // 10 second timeout

            // Import pdfjs dynamically to avoid loading in non-PDF contexts
            import('react-pdf').then((pdfModule) => {
                const { pdfjs } = pdfModule;
                const fileReader = new FileReader();
                
                fileReader.onerror = () => {
                    clearTimeout(timeout);
                    console.error('Error reading file for PDF page count');
                    // Allow on error - let the backend handle validation
                    resolve({ allowed: true });
                };

                fileReader.onload = async (e) => {
                    try {
                        clearTimeout(timeout);
                        const arrayBuffer = e.target?.result as ArrayBuffer;
                        if (!arrayBuffer) {
                            resolve({ allowed: true });
                            return;
                        }

                        const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
                        const pdf = await loadingTask.promise;
                        const numPages = pdf.numPages;

                        if (numPages > limits.maxDocumentPages!) {
                            resolve({
                                allowed: false,
                                reason: `Your plan allows documents up to ${limits.maxDocumentPages} pages. This document has ${numPages} pages. Upgrade to analyze larger documents.`,
                                maxPages: limits.maxDocumentPages,
                            });
                        } else {
                            resolve({ allowed: true });
                        }
                    } catch (error) {
                        clearTimeout(timeout);
                        console.error('Error reading PDF:', error);
                        // Allow on error - let the backend handle validation
                        resolve({ allowed: true });
                    }
                };
                
                fileReader.readAsArrayBuffer(file);
            }).catch((error) => {
                clearTimeout(timeout);
                console.warn('PDF.js not available for page counting:', error);
                // If PDF.js fails to load, allow and let backend validate
                resolve({ allowed: true });
            });
        } else {
            // For non-PDF files, allow (backend will handle validation)
            resolve({ allowed: true });
        }
    });
}

/**
 * Check if user can create a new case (based on case limit)
 */
export async function canCreateCase(
    userId: string,
    planName: PlanName | string | null | undefined
): Promise<{ allowed: boolean; reason?: string; currentCount?: number; maxCases?: number }> {
    const limits = getPlanLimits(planName);

    // Unlimited plans
    if (limits.maxCases === null) {
        return { allowed: true };
    }

    try {
        const { count, error } = await supabase
            .from('cases')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userId);

        if (error) {
            console.error('Error checking case limit:', error);
            // Allow on error to avoid blocking users
            return { allowed: true };
        }

        const caseCount = count || 0;

        if (caseCount >= limits.maxCases) {
            return {
                allowed: false,
                reason: `You've reached your case limit of ${limits.maxCases} cases. Upgrade to create unlimited cases.`,
                currentCount: caseCount,
                maxCases: limits.maxCases,
            };
        }

        return {
            allowed: true,
            currentCount: caseCount,
            maxCases: limits.maxCases,
        };
    } catch (error) {
        console.error('Error checking case limit:', error);
        // Allow on error to avoid blocking users
        return { allowed: true };
    }
}

/**
 * Get user's current plan name from profile/subscription
 * 
 * @param profile - User profile object with optional subscription data
 * @returns Plan name, defaults to 'free' if no subscription found
 */
export function getUserPlanName(profile: Profile | null): PlanName {
    if (!profile?.subscription?.plan_name) {
        return 'free';
    }
    return profile.subscription.plan_name as PlanName;
}

/**
 * Check if a feature is available for the user's plan
 */
export function hasFeature(
    planName: PlanName | string | null | undefined,
    feature: keyof Omit<SubscriptionLimits, 'maxChatsPerDay' | 'maxDocumentPages' | 'maxCases'>
): boolean {
    const limits = getPlanLimits(planName);
    return limits[feature] === true;
}
